import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { CurrentUser } from "@/lib/api";

export function ProfileDropdown({
  user,
  onLogout,
  onOpenCategories,
  onOpenBrands,
  onOpenAnnouncements,
}: {
  user: CurrentUser;
  onLogout: () => void;
  onOpenCategories: () => void;
  onOpenBrands: () => void;
  onOpenAnnouncements: () => void;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [isOpen]);

  // Only routes that actually exist in this app show up here - no
  // profile/orders/customers/etc. pages to link to yet.
  const menuItems = [
    {
      label: t("nav.addProduct"),
      onClick: () => navigate("/products/new"),
      show: user.is_superuser,
      isLogout: false,
    },
    {
      label: t("nav.categories"),
      onClick: onOpenCategories,
      show: user.is_superuser,
      isLogout: false,
    },
    {
      label: t("nav.brands"),
      onClick: onOpenBrands,
      show: user.is_superuser,
      isLogout: false,
    },
    {
      label: t("nav.announcements"),
      onClick: onOpenAnnouncements,
      show: user.is_superuser,
      isLogout: false,
    },
    {
      label: t("nav.logOut"),
      onClick: onLogout,
      show: true,
      isLogout: true,
    },
  ];

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        className="flex items-center gap-2 rounded-full px-1 py-1 bg-brand text-white hover:bg-brand/90 transition-colors duration-200 cursor-pointer"
      >
        <span className="flex items-center justify-center size-7 rounded-full bg-white/20 text-xs font-bold uppercase">
          {user.email.slice(0, 1)}
        </span>
      </button>
      <div
        className={`absolute top-full right-0 mt-2 bg-app-surface border border-app-border text-foreground w-48 rounded-2xl shadow-lg z-50 overflow-hidden transition-all duration-200 ease-in-out ${
          isOpen ? "opacity-100 translate-y-0 scale-100" : "opacity-0 -translate-y-2 scale-95 pointer-events-none"
        }`}
      >
        {user.is_superuser && (
          <div className="px-4 py-2 text-xs font-semibold text-brand uppercase tracking-wide border-b border-gray-100">
            {t("nav.admin")}
          </div>
        )}
        <ul>
          {menuItems.filter(item => item.show).map(item => (
            <li key={item.label}>
              <button
                type="button"
                className={`text-left px-4 py-3 w-full transition-colors duration-200 cursor-pointer ${
                  item.isLogout ? "text-red-500 hover:bg-red-500/10" : "text-foreground hover:bg-app-surface-hover"
                }`}
                onClick={() => {
                  setIsOpen(false);
                  item.onClick();
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
